/**
 * Which file production places for a custom logo (AB#20790).
 *
 * Three candidates exist per logo:
 *  - the ORIGINAL upload (`original_logo_url`) — a raster, or already a vector;
 *  - the AUTO trace vectorizer.ai produced when the order was placed
 *    (`vectorized_logo_info.vectorized_file_url`, an .svg under `vectorizer/`),
 *    or, when that is missing, the on-demand `download/vectorized-logo` endpoint
 *    (which traces only if nothing is cached on S3 — every trace costs a credit);
 *  - a MANUAL vector a designer uploaded next to the logo
 *    (`{dir}/{name}_vectorized_manual.{svg|pdf|eps|ai}`).
 *
 * Staff pick one in the Logo inspector; the pick is stored on the order as
 * `vectorized_logo_info.production_source`. Without a pick the order is
 * manual > vector original > auto — the same precedence the backend's download
 * endpoint applies. Only .svg can be inlined into the production file; a
 * pdf/eps/ai pick falls back to the auto trace and is reported.
 */
import type { CustomLogo, LogoVectorSource } from '@/models/CustomLogo';
import { normalizeStorageKey } from './logoVectorize';

export const LOGO_VECTOR_SOURCES: LogoVectorSource[] = ['auto', 'manual', 'original'];

/** What `GET admin/manual-vectorized-logo-info?path=` returns. */
export interface ManualVectorInfo {
  has_manual: boolean;
  extension: string | null;
  meta?: {
    uploaded_by_name?: string;
    uploaded_by_email?: string;
    uploaded_at?: string;
    file_name?: string;
    extension?: string;
  } | null;
}

export function fileExtension(path: string | null | undefined): string {
  const clean = normalizeStorageKey(path ?? '');
  const m = clean.match(/\.([A-Za-z0-9]+)$/);
  return m ? m[1].toLowerCase() : '';
}

export function isVectorPath(path: string | null | undefined): boolean {
  return ['svg', 'pdf', 'eps', 'ai'].includes(fileExtension(path));
}

export function isSvgPath(path: string | null | undefined): boolean {
  return fileExtension(path) === 'svg';
}

export function isRasterPath(path: string | null | undefined): boolean {
  return ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'tiff', 'tif', 'webp'].includes(fileExtension(path));
}

/** The key the builder placed — what the composite embeds and `customLogoKeys` carries. */
export function placedLogoKey(logo: CustomLogo): string | null {
  const key = normalizeStorageKey(logo.url ?? '');
  return key || null;
}

/** The upload as the customer provided it. */
export function originalLogoKey(logo: CustomLogo): string | null {
  const key = normalizeStorageKey(logo.original_logo_url ?? logo.original_logo ?? logo.url ?? '');
  return key || null;
}

/**
 * The key a designer's manual vector is filed next to. MUST match the customer
 * PDF (customer-order-pdf.blade.php): the original when it is already a vector,
 * else the placed key — or the inspector looks in the wrong folder.
 */
export function manualVectorBaseKey(logo: CustomLogo): string | null {
  const original = originalLogoKey(logo);
  if (original && isVectorPath(original)) return original;
  return placedLogoKey(logo);
}

/** `{dir}/{name}_vectorized_manual.{ext}` beside `baseKey` (backend FileController::manualVectorPath). */
export function manualVectorKey(baseKey: string, extension: string): string {
  const slash = baseKey.lastIndexOf('/');
  const dir = slash >= 0 ? baseKey.slice(0, slash + 1) : '';
  const file = slash >= 0 ? baseKey.slice(slash + 1) : baseKey;
  const dot = file.lastIndexOf('.');
  const name = dot > 0 ? file.slice(0, dot) : file;
  return `${dir}${name}_vectorized_manual.${extension.toLowerCase()}`;
}

/** The auto trace as an .svg storage key, when the order carries a usable one. */
export function autoVectorKey(logo: CustomLogo): string | null {
  const info = logo.vectorized_logo_info;
  if (!info || info.status === 'failed') return null;
  const key = normalizeStorageKey(info.vectorized_file_url ?? '');
  return key && isSvgPath(key) ? key : null;
}

export function storedLogoSource(logo: CustomLogo): LogoVectorSource | null {
  const value = logo.vectorized_logo_info?.production_source;
  return value && LOGO_VECTOR_SOURCES.includes(value) ? value : null;
}

/** What production uses when nobody chose: manual > vector original > auto. */
export function defaultLogoSource(
  logo: CustomLogo,
  manual?: ManualVectorInfo | null
): LogoVectorSource {
  if (manual?.has_manual) return 'manual';
  const original = originalLogoKey(logo);
  if (original && isVectorPath(original)) return 'original';
  return 'auto';
}

export function effectiveLogoSource(
  logo: CustomLogo,
  manual?: ManualVectorInfo | null
): LogoVectorSource {
  return storedLogoSource(logo) ?? defaultLogoSource(logo, manual);
}

export interface LogoProductionResolution {
  /** The choice that applied (stored or default). */
  source: LogoVectorSource;
  /** `svg` = fetch `url` and inline it; `raster` = keep the embedded upload. */
  kind: 'svg' | 'raster';
  url: string | null;
  /** Warning code when the choice could not be honoured literally. */
  note?: string;
}

export interface LogoResolveOptions {
  /** Storage prefix with trailing slash. */
  storageUrl: string;
  /** Builds the on-demand auto-trace URL for a raster key. */
  vectorizeUrl: (s3Key: string) => string;
  manual?: ManualVectorInfo | null;
}

export function resolveLogoProduction(
  logo: CustomLogo,
  opts: LogoResolveOptions
): LogoProductionResolution {
  const source = effectiveLogoSource(logo, opts.manual);
  const placed = placedLogoKey(logo);

  const auto = (): LogoProductionResolution => {
    const key = autoVectorKey(logo);
    if (key) return { source: 'auto', kind: 'svg', url: `${opts.storageUrl}${key}` };
    if (placed && !isSvgPath(placed)) {
      return { source: 'auto', kind: 'svg', url: opts.vectorizeUrl(placed) };
    }
    return placed
      ? { source: 'auto', kind: 'svg', url: `${opts.storageUrl}${placed}` }
      : { source: 'auto', kind: 'raster', url: null };
  };

  if (source === 'original') {
    const original = originalLogoKey(logo);
    if (original && isSvgPath(original)) {
      return { source, kind: 'svg', url: `${opts.storageUrl}${original}` };
    }
    // A pdf/eps/ai original cannot be inlined into the SVG; the raster the
    // builder placed stays, so the file is still what the customer approved.
    return {
      source,
      kind: 'raster',
      url: null,
      note:
        original && isVectorPath(original) ? `logo-original-vector-not-svg:${original}` : undefined,
    };
  }

  if (source === 'manual') {
    const base = manualVectorBaseKey(logo);
    const ext = opts.manual?.extension?.toLowerCase() ?? null;
    if (base && opts.manual?.has_manual && ext === 'svg') {
      return { source, kind: 'svg', url: `${opts.storageUrl}${manualVectorKey(base, 'svg')}` };
    }
    const fallback = auto();
    return {
      ...fallback,
      source,
      note: opts.manual?.has_manual
        ? `logo-manual-vector-not-svg:${base ?? placed ?? ''}`
        : `logo-manual-vector-missing:${base ?? placed ?? ''}`,
    };
  }

  return auto();
}

/**
 * The `resolveUrl` hook for inlineCustomLogoVectors, built for a product's
 * logos: placed key -> URL to fetch, `null` to keep the raster, `undefined`
 * for logos the product does not know. Notes (warning codes) for choices that
 * could not be honoured are appended to `notes`.
 */
export function buildLogoUrlResolver(
  logos: CustomLogo[],
  opts: Omit<LogoResolveOptions, 'manual'>,
  manualByKey: Map<string, ManualVectorInfo | null>,
  notes?: string[]
): (s3Key: string) => string | null | undefined {
  const byKey = new Map<string, LogoProductionResolution>();
  for (const logo of logos) {
    const key = placedLogoKey(logo);
    if (!key) continue;
    const resolution = resolveLogoProduction(logo, {
      ...opts,
      manual: manualByKey.get(key) ?? null,
    });
    byKey.set(key, resolution);
    if (resolution.note) notes?.push(resolution.note);
    if (resolution.kind === 'raster' && resolution.source === 'original') {
      notes?.push(`logo-kept-original-raster:${key}`);
    }
  }
  return (s3Key: string) => {
    const resolution = byKey.get(normalizeStorageKey(s3Key));
    if (!resolution) return undefined;
    return resolution.kind === 'raster' ? null : resolution.url;
  };
}
